import React, { useEffect, useState, useMemo } from "react";
import { ActionIcon, Button, Collapse, Text } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import MainTable from 'app/views/components/Table/MainTable';
import { columnDefsTransaction } from "./sub-trade-entry-panel-facility-transaction-data-columnDefs";
import { formatPriceWithoutCurrency } from "app/utils/util-functions";
import { RefinedSubTrade, SubTradeFacilityFeeData, TradedFacilityParams } from "app/models/traded-facilities-params";
import { FacilityAmountData } from "app/models/facility-params";
import moment from 'moment'
import { MasterTradeParams } from "app/models/master-trade-params";
import { useListState } from "@mantine/hooks";
import SubTradeFacilityFeeModal from "./sub-trade-facility-fee-modal";
import SubTradeFacilityFeeDrawer from "./sub-trade-facility-fee-drawer";
import { columnDefsAccrualFee } from "./sub-trade-entry-panel-facility-accrual-fee-data-columnDefs";
import { autoCalculatedFees } from "app/models/dropdown-options";
import OverrideSubTradeFacilityFeeModal from "./override-sub-trade-facility-fee-modal";
import { SignableDocument } from "app/models/document-params";
import { getFundedAndUnfundedTradeAmount, getAppliedPikAmount } from "app/views/pages/trade-entry/utils/master-trade-util";
import { FMFacilityAmounts, FMFeeSections } from "app/models/fm-metadata-params";
import { feeSectionNotes, isFeeSectionFull } from "./fee-section-notes";

interface FacilityPanelProps {
    facility: TradedFacilityParams;
    fmSignableDoc: SignableDocument | null;
    aaSignableDoc: SignableDocument | null;
    masterTrade: MasterTradeParams;
    subTrade: RefinedSubTrade;
    refinedSubTradeFacilityFees: SubTradeFacilityFeeData[];
    fmFacilityAmounts?: FMFacilityAmounts[];
    /** Funding Memo fee-section states; a DC / CoC row is replaced by the memo's statement when nil. */
    fmFeeSections?: FMFeeSections;
    fmLocked?: boolean;
    expended: boolean;
    setExpended: (expanded: boolean) => void;
}

const FacilityPanel: React.FC<FacilityPanelProps> = React.memo(({ facility, fmSignableDoc, aaSignableDoc, masterTrade, subTrade, refinedSubTradeFacilityFees, fmFacilityAmounts, fmFeeSections, fmLocked, expended, setExpended }) => {
    const [facilityAmounts, facilityAmountsHandler] = useListState<FacilityAmountData>();
    const [loader, setLoader] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [openOverrideModal, setOpenOverrideModal] = useState(false)
    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedFee, setSelectedFee] = useState(null);
    const [totalAmount, setTotalAmount] = useState(0.0);

    const topTypes = ["Cost of Carry", "Economic Benefit", "Delayed Compensation"];

    const isEditable =
        subTrade.status !== 'Canceled' &&
        (fmSignableDoc === null || fmSignableDoc.signatures?.length === 0) &&
        !fmLocked;

    // Use facility amounts from FM metadata endpoint
    const fmAmountsForFacility = useMemo(() => {
        const match = fmFacilityAmounts?.find(fa => fa.facilityId.id === facility.facilityId.id)
        if (!match) return []
        return match.facilityAmounts.map(fa => ({
            amount: fa.amount,
            actionType: fa.actionType ?? '',
            effectiveDate: fa.effectiveDate,
            loanUtilizedAmount: fa.loanUtilizedAmount,
            locUtilizedAmount: fa.locUtilizedAmount,
            processedTime: fa.processedTime,
            transactionId: fa.transactionId,
            locIds: fa.locIds,
            loanIds: fa.loanIds,
            feeHistoryData: fa.feeHistoryData,
        } as FacilityAmountData))
    }, [fmFacilityAmounts, facility.facilityId.id])

    useEffect(() => {
        facilityAmountsHandler.setState(fmAmountsForFacility);
    }, [fmAmountsForFacility]);

    const feeTransactionAmount = useMemo(() => {
        if (masterTrade.paymentType !== 'Standard') return 0.0;
        const facilityAmount = subTrade.lockedDate ?
            facility.amounts?.findLast((amountWithDate) => new Date(amountWithDate.effectiveDate) <= new Date(subTrade.lockedDate ?? ''))
            : facility.amounts?.[facility.amounts.length - 1];

        const appliedPikAmount = getAppliedPikAmount(
            facility.amounts,
            masterTrade.tradeDate ?? undefined,
            subTrade.targetSettlementDate,
        )

        // PIK travels free: pro-rata on full amount first, then subtract PIK
        // Correct formula: price × (fundedPrincipal - PIK)  NOT: price × (amount - PIK) × fundedProportion
        const { fundedAmount: rawFundedAmount, unfundedAmount } = getFundedAndUnfundedTradeAmount(
            Number(facilityAmount?.amount ?? 0),
            Number(facility.price),
            subTrade.lockedDate ? new Date(subTrade.lockedDate) : subTrade.targetSettlementDate ? new Date(subTrade.targetSettlementDate) : null,
            [{
                facilityId: facility.facilityId,
                facilityAmounts: facilityAmounts
            }],
            facility.facilityId.id
        );
        // Subtract PIK after pro-rata: fundedAmount = rawFundedAmount - PIK × price/100
        const fundedAmount = rawFundedAmount - (appliedPikAmount * Number(facility.price) / 100)
        return fundedAmount + unfundedAmount;
    }, [facility.amounts, facility.price, masterTrade.paymentType, masterTrade.tradeDate, subTrade.targetSettlementDate, facilityAmounts]);

    // Funding Memo parity: a fee the memo states a nil for (never opened / not applicable) has no
    // row here either — the statement below the header takes its place. FULL keeps the row, even at 0.00.
    const nilFeeNotes = useMemo(() => feeSectionNotes(fmFeeSections), [fmFeeSections]);

    const feeTableData = useMemo(() => {

        const data = refinedSubTradeFacilityFees
            .filter((fee) => isFeeSectionFull(fee.feeType, fmFeeSections))
            .map((fee, index) => {
            return {
                id: index,
                type: fee.feeType,
                otherAmount: fee.otherAmount,
                paidTo: fee.paidTo,
                overrideAmount: fee.overrideAmount,
                reason: fee.reason,
                facilityId: facility.facilityId.id,
                subTradeId: facility.contractId?.id ?? "",
                status: subTrade.status,
                isEditable,
            };
        });

        data.sort((a, b) => {
            const aTopIndex = topTypes.indexOf(a.type);
            const bTopIndex = topTypes.indexOf(b.type);

            if (aTopIndex !== -1 && bTopIndex !== -1) {
                return aTopIndex - bTopIndex;
            }
            // If only one of them is a top type, that one comes first.
            if (aTopIndex !== -1) return -1;
            if (bTopIndex !== -1) return 1;
            // Otherwise, sort them alphabetically by type.
            return a.type.localeCompare(b.type);
        });

        return data;
    }, [facility, refinedSubTradeFacilityFees, fmFeeSections]);

    useEffect(() => {
        const total = feeTableData.reduce((acc, fee) => {
            return fee.paidTo === "PaidToSeller" ? acc + Number(fee.overrideAmount) : acc - Number(fee.overrideAmount);
        }, 0);
        setTotalAmount(total);
    }, [feeTableData])

    const transactionTable = useMemo(() => {
        if (!facilityAmounts.length) return [];
        setLoader(true);
        const filteredAmounts = facilityAmounts.filter(
            (facilityAmount: FacilityAmountData) => {
                const facilityDate = new Date(facilityAmount.effectiveDate);
                facilityDate.setUTCHours(0, 0, 0, 0);

                const tradeDate = new Date(masterTrade.tradeDate ?? "");
                tradeDate.setUTCHours(0, 0, 0, 0);

                return facilityDate >= tradeDate &&
                    (!subTrade.lockedDate || new Date(facilityAmount.effectiveDate) <= new Date(subTrade.lockedDate)) &&
                    ["FacilityPaydown", "FacilityPIK"].includes(facilityAmount.actionType);
            }
        );
        const result = [
            {
                id: "1",
                date: masterTrade.tradeDate,
                type: "Trade",
                originalValue: formatPriceWithoutCurrency(Number(facility.tradeAmount), 2, 2),
                updatedValue: formatPriceWithoutCurrency(Number(facility.tradeAmount), 2, 2),
                appliedToTrade: "Yes",
                masterTradeId: { id: masterTrade.id, admin: masterTrade.agencyAdmin },
                facilityId: facility.facilityId,
                subTradeId: undefined as any,
                positionAtDate: undefined as any,
                isEditable: false,
                isValueOverridden: false,
                changeAmount: 0.0,
                aaSignableDoc: aaSignableDoc,
                transactionId: null as string | null,
                facilityAmountData: null as FacilityAmountData | null,
            },
        ];

        // Sort facility amounts by effectiveDate then processedTime for display ordering
        const sortedFacilityAmounts = [...filteredAmounts].sort((a, b) => {
            const dateCompare = new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime();
            if (dateCompare !== 0) return dateCompare;
            return (
                new Date(a.processedTime ?? a.effectiveDate).getTime() -
                new Date(b.processedTime ?? b.effectiveDate).getTime()
            );
        });

        // Split traded amounts into transactionId-matched and legacy (null transactionId)
        const nonCreationTradeAmounts = (facility?.amounts ?? [])
            .filter(a => a.transaction !== 'TradeCreationTradeTransaction');
        const tradeAmountsWithTxId = nonCreationTradeAmounts.filter(a => (a as any).transactionId != null);
        const tradeAmountsWithoutTxId = nonCreationTradeAmounts
            .filter(a => (a as any).transactionId == null)
            .sort((a, b) => new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime());

        // Split legacy trade amounts by type so PIK rows only match PIKTradeTransaction
        // and Paydown rows only match PaydownTradeTransaction. Without this split, a shared
        // positional counter causes a FacilityPIK to steal the PaydownTradeTransaction slot,
        // leaving the FacilityPaydown row unmatched and showing "Apply now" incorrectly.
        const tradePaydownAmountsWithoutTxId = tradeAmountsWithoutTxId
            .filter(a => a.transaction === 'PaydownTradeTransaction');
        const tradePikAmountsWithoutTxId = tradeAmountsWithoutTxId
            .filter(a => a.transaction === 'PIKTradeTransaction');

        // Separate position counters per action type + date for legacy matching
        const paydownCountByDate = new Map<string, number>();
        const pikCountByDate = new Map<string, number>();

        sortedFacilityAmounts.forEach((facilityAmount: FacilityAmountData) => {
            let tradeChange: typeof nonCreationTradeAmounts[0] | undefined;
            let positionInDate: number | undefined

            // Prefer direct transactionId matching when both sides carry one. This covers
            // modern trades and also newly-applied rows on legacy trades (where older TF
            // amounts have no transactionId but newly-added ones do).
            if (facilityAmount.transactionId != null) {
                tradeChange = tradeAmountsWithTxId.find(
                    a => (a as any).transactionId === facilityAmount.transactionId
                );
                if (tradeChange) {
                    // Per-type position to match DAML's per-type indexing in OverrideAmountAtDate.
                    // Without filtering by transaction type, same-date entries of different
                    // types would offset each other's positions and DAML would target the wrong row.
                    const matchedTransaction = tradeChange.transaction
                    const sortedAmounts = [...(facility?.amounts ?? []) as any[]].sort(
                        (a, b) => new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime()
                    )
                    const atDateOfType = sortedAmounts.filter(a =>
                        moment(a.effectiveDate).isSame(moment(facilityAmount.effectiveDate), 'day')
                        && a.transaction === matchedTransaction
                    )
                    const idx = atDateOfType.findIndex(a => a.transactionId === facilityAmount.transactionId)
                    if (idx < 0) {
                        // Data inconsistency: tradeChange was matched by txId above, but the same entry
                        // is not findable in the date+type filtered view. Falling back to 0 here would
                        // target the wrong row on the contract side — surface this for debugging.
                        console.warn(
                            `[FacilityPanel] positionInDate lookup missed for txId=${facilityAmount.transactionId}, defaulting to 0`
                        )
                    }
                    positionInDate = idx >= 0 ? idx : 0
                }
            }

            // Fallback: position-based matching against legacy (no-txId) trade amounts.
            // Runs when the facility row has no transactionId, or when txId lookup missed
            // (e.g. facility has a txId but the TF side is still legacy pre-feature data).
            if (!tradeChange) {
                const dateKey = moment(facilityAmount.effectiveDate).format('YYYY-MM-DD');
                const isPaydown = facilityAmount.actionType === 'FacilityPaydown';
                const countMap = isPaydown ? paydownCountByDate : pikCountByDate;
                const tradeList = isPaydown ? tradePaydownAmountsWithoutTxId : tradePikAmountsWithoutTxId;

                positionInDate = countMap.get(dateKey) ?? 0;
                countMap.set(dateKey, positionInDate + 1);
                let currentPos = 0;
                for (const amount of tradeList) {
                    if (moment(amount.effectiveDate).isSame(moment(facilityAmount.effectiveDate), 'day')) {
                        if (currentPos === positionInDate) { tradeChange = amount; break; }
                        currentPos++;
                    }
                }
            }

            const globalIndex = facilityAmounts.findIndex((item) => item === facilityAmount);
            const index = filteredAmounts.indexOf(facilityAmount);

            // Check if agent has signed - treat as non-editable
            const hasAgentSignature = aaSignableDoc?.signatures?.some(
                (sig) => sig.signerType === 'Agent'
            ) ?? false;

            result.push({
                id: "",
                date: facilityAmount.effectiveDate.toString(),
                type: facilityAmount.actionType === "FacilityPaydown" ? "Paydown" : "PIK",
                originalValue: result[index].updatedValue,
                updatedValue: (tradeChange ? formatPriceWithoutCurrency(Number(tradeChange.amount), 2, 2) : result[index].updatedValue),
                appliedToTrade: tradeChange ? "Yes" : (isEditable && !hasAgentSignature && subTrade.status !== 'Settled' && !fmLocked) ? "Apply now" : "No",
                masterTradeId: { id: masterTrade?.id ?? "", admin: masterTrade?.agencyAdmin ?? "" },
                facilityId: facility.facilityId,
                subTradeId: facility.contractId,
                positionAtDate: positionInDate,
                isEditable: isEditable && !hasAgentSignature && subTrade.status !== 'Settled' && !fmLocked,
                isValueOverridden: tradeChange?.isOverridden ?? false,
                changeAmount: globalIndex > 0 ? Number(facilityAmount.amount) - Number(facilityAmounts[globalIndex - 1].amount) : 0.0,
                aaSignableDoc: aaSignableDoc,
                transactionId: facilityAmount.transactionId ?? null,
                facilityAmountData: facilityAmount,
            });
        });

        setLoader(false);
        return result;
    }, [facilityAmounts, masterTrade, facility.tradeAmount, facility.amounts, fmLocked]);

    return (
        <div key={facility.facilityId.id} className="panel-box-facilities">
            <div className={`panel-header ${expended ? "panel-header-open" : ""}`} onClick={() => setExpended(!expended)}>
                <ActionIcon variant="transparent">
                    {expended ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
                </ActionIcon>

                <div className="panel-header-content">
                    <Text className="panel-box-title">
                        {facility?.facilityDetails?.name} ({facility?.facilityDetails?.cusip})
                    </Text>
                    <Text className="panel-box-title">
                        {`${formatPriceWithoutCurrency(feeTransactionAmount + totalAmount, 2, 2)} ${facility.facilityDetails?.baseCurrency ?? 'USD'}`}
                    </Text>
                </div>
            </div>

            <Collapse in={expended} style={{ width: "100%" }}>
                <div className="facility-title-view">
                    <Text className="facility-title-text">Transactions</Text>
                </div>
                <MainTable
                    tableName="Transactions Table Data"
                    columnDefs={columnDefsTransaction}
                    data={transactionTable}
                    setRow={() => { }}
                    headerBackgroundColor="#F0EEEE"
                    disableStriping
                    enableTopToolbar={false}
                    withPagination={false}
                    csvExportEnabled={false}
                    enableColumnResizing={false}
                    enableColumnOrdering={false}
                    enableGrouping={false}
                    minHeight="0px"
                    isLoading={loader}
                />

                <div className="facility-title-view panel-header-content" style={{ marginTop: "15px" }}>
                    <Text className="facility-title-text">Accruing Fees</Text>
                    <Text className="accruing-fees-view-calculation" onClick={() => { setOpenDrawer(true) }}>View calculations</Text>
                </div>
                {nilFeeNotes.map((note) => (
                    <Text key={note.feeType} className="accruing-fees-nil-note">
                        <span className="accruing-fees-nil-note-fee">{note.feeType}:</span> {note.statement}
                    </Text>
                ))}

                {columnDefsAccrualFee.length !== 0 && (
                    <MainTable
                        tableName="Accruing Fees Table Data"
                        columnDefs={columnDefsAccrualFee}
                        data={feeTableData}
                        setRow={(e: any, cellName: string) => {
                            if (!isEditable) return;
                            if (cellName === "Delete") return;

                            setSelectedFee(e)
                            if (autoCalculatedFees.includes(e.type)) {
                                setOpenOverrideModal(true)
                                return
                            }
                            setOpenModal(true);

                        }}
                        headerBackgroundColor="#F0EEEE"
                        enableTopToolbar={false}
                        disableStriping
                        withPagination={false}
                        csvExportEnabled={false}
                        enableColumnResizing={false}
                        enableColumnOrdering={false}
                        enableGrouping={false}
                        minHeight="0px"
                        enableSorting={false}
                    />
                )}

                <div className="accruing-fees-total">
                    <Text className="accruing-fees-total-text">Total:</Text>
                    <Text className="accruing-fees-total-amount">{formatPriceWithoutCurrency(Math.abs(totalAmount), 2, 2)}</Text>
                </div>
                {isEditable && (
                    <>
                        <div className="action-button-add-fee-div">
                            <Button
                                className="action-button-add-fee-button"
                                disabled={!isEditable}
                                onClick={() => {
                                    setOpenModal(true);
                                    setSelectedFee(null)
                                }}
                            >
                                Add Fee
                            </Button>
                        </div>

                        <OverrideSubTradeFacilityFeeModal fee={selectedFee} opened={openOverrideModal} close={() => setOpenOverrideModal(false)} reopen={() => setOpenOverrideModal(true)} facilityId={facility.facilityId} subTradeId={facility.contractId ?? { id: "", admin: "" }} />
                        <SubTradeFacilityFeeModal fee={selectedFee} opened={openModal} close={() => setOpenModal(false)} facilityId={facility.facilityId} subTradeId={facility.contractId ?? { id: "", admin: "" }} />
                    </>
                )}
                <SubTradeFacilityFeeDrawer open={openDrawer} setOpen={setOpenDrawer} facilityId={facility.facilityId} subTrade={subTrade} feeSections={fmFeeSections} />

            </Collapse>
        </div>
    );
});

export default FacilityPanel;